// src/components/StepUpGate.tsx
/**
 * Wraps an admin page so it is only rendered to a session that has passed its second factor.
 *
 * WHAT IT IS NOT: a security control. The server decides — `@AdminStepUp` on the endpoints, and
 * `AuthorizationService.isAdminSessionMfaVerified()` behind it. This wrapper exists so that an admin
 * who has to verify is ASKED, in one place, instead of watching every panel on the page fail with a
 * 403 it cannot explain. Everything below can be defeated by editing the store in a console, and
 * doing so buys nothing: every request the page then makes is still refused.
 *
 * THE ORDINARY GATE FAILS OPEN, ON PURPOSE. If the status cannot be read at all — a network blip,
 * or a legacy admin
 * whose `is_admin` flag says admin while `c_role` does not, which answers `403 forbidden` from
 * `/v1/mfa/status` — the page renders with a banner rather than a lock. A client-side gate that
 * fails closed would be the second lock in this project with the key on the inside, and it would
 * protect nothing the server is not already protecting. The {@code strict} form fails closed
 * because it fronts endpoints carrying {@code @SecondFactorRequired}; it offers a retry instead of
 * issuing a sensitive request without knowing whether the session completed TOTP.
 *
 * NON-ADMINS PASS STRAIGHT THROUGH. a member page that also serves the owner
 * (an admin gets the whole table through `/orders/all`, which is step-up gated), so the gate must be
 * invisible — and must fire no requests at all — for the ~1,300 accounts that are not admins.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
    closeStepUp,
    openStepUp,
    selectMfa,
    selectMfaIsCurrent,
    selectNeedsStepUp,
} from '../store/mfaSlice';
import { loadMfaStatus } from '../store/mfaThunks';
import type { AppDispatch, RootState } from '../store/store';

interface StepUpGateProps {
    /** Show the "turn it on" banner to an admin who has not enrolled. Off for member pages,
     *  which an admin opens to read one invoice, and on for the two owner-only pages. */
    nudge?: boolean;
    /** Require a verified factor even while the server-wide rollout flag is off. Use only for a
     *  surface whose backend carries `@SecondFactorRequired`. */
    strict?: boolean;
    children: React.ReactNode;
}

/**
 * sessionStorage, not localStorage: "not now" should last for this sitting, not forever. Enrolment
 * is the one thing this whole batch exists to get done, so a permanently dismissed banner would be
 * a permanently forgotten task.
 */
export const MFA_NUDGE_DISMISSED_KEY = 'mfaNudgeDismissed';

/** Storage access throws outright in a Safari private window, and a banner is not worth a blank
 *  page — both directions of this are wrapped. */
const readNudgeDismissed = (): boolean => {
    try {
        return sessionStorage.getItem(MFA_NUDGE_DISMISSED_KEY) === '1';
    } catch {
        return false;
    }
};

const StepUpGate: React.FC<StepUpGateProps> = ({ nudge = false, strict = false, children }) => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { user, isAuthenticated, accessToken } = useSelector((state: RootState) => state.auth);
    const mfa = useSelector(selectMfa);
    const isCurrent = useSelector(selectMfaIsCurrent);
    const needsStepUp = useSelector(selectNeedsStepUp);
    const [nudgeDismissed, setNudgeDismissed] = useState(readNudgeDismissed);

    // The API supplies its canonical admin indicator. Endpoint authorization remains authoritative;
    // this display flag only decides whether to offer the verification UI.
    const isAdmin = Boolean(isAuthenticated && user?.isAdmin);

    /** Per token, so a new login re-asks. The thunk itself no-ops for anyone who is not a signed-in
     *  admin and dedupes concurrent calls, so two gated components on one page cost one request. */
    useEffect(() => {
        if (isAdmin && accessToken) {
            void dispatch(loadMfaStatus());
        }
    }, [isAdmin, accessToken, dispatch]);

    /**
     * Raise the blocking prompt once, and take it back down on the way out.
     *
     * The ref is what makes "once" true: without it, a reader who somehow closes the prompt would
     * have it reopened by the next render. Without the unmount cleanup, pressing Back on a blocked
     * page would leave the modal floating over whatever page came next.
     */
    const openedRef = useRef(false);
    const shouldPrompt = needsStepUp || (strict && isCurrent && mfa.loadState === 'ready'
        && mfa.enabled && !mfa.sessionVerified);
    useEffect(() => {
        if (!shouldPrompt) {
            openedRef.current = false;
            return;
        }
        if (openedRef.current) {
            return;
        }
        openedRef.current = true;
        dispatch(openStepUp({ blocking: true }));
    }, [shouldPrompt, dispatch]);

    useEffect(
        () => () => {
            if (openedRef.current) {
                dispatch(closeStepUp());
            }
        },
        [dispatch]
    );

    const dismissNudge = () => {
        try {
            sessionStorage.setItem(MFA_NUDGE_DISMISSED_KEY, '1');
        } catch {
            // Private mode. The banner still goes away for this render; it will be back next visit.
        }
        setNudgeDismissed(true);
    };

    if (!isAdmin) {
        return <>{children}</>;
    }

    // Nothing has been read for THIS token yet. Deliberately a placeholder rather than the page:
    // rendering the page first would fire its own admin requests before anyone knew whether this
    // session could make them, and the reader would see a wall of failures instead of one prompt.
    if (!isCurrent || mfa.loadState === 'idle' || mfa.loadState === 'loading') {
        return <Placeholder>Overujem reláciu…</Placeholder>;
    }

    if (mfa.loadState === 'error') {
        if (strict) {
            return (
                <Placeholder>
                    <PlaceholderText>{mfa.loadError}</PlaceholderText>
                    <Primary type="button" onClick={() => void dispatch(loadMfaStatus({ force: true }))}>
                        Skúsiť znova
                    </Primary>
                </Placeholder>
            );
        }
        return (
            <>
                <Notice>
                    <Banner>{mfa.loadError} Stránka sa zobrazuje bez overenia.</Banner>
                    <Secondary type="button" onClick={() => void dispatch(loadMfaStatus({ force: true }))}>
                        Skúsiť znova
                    </Secondary>
                </Notice>
                {children}
            </>
        );
    }

    if (strict && !mfa.enabled) {
        return (
            <Placeholder>
                <PlaceholderText>
                    Táto správcovská stránka vyžaduje zapnuté dvojstupňové overenie.
                </PlaceholderText>
                <Primary type="button" onClick={() => navigate('/bezpecnost')}>
                    Nastaviť dvojstupňové overenie
                </Primary>
            </Placeholder>
        );
    }

    if ((strict || mfa.enabled) && !mfa.sessionVerified) {
        return (
            <Placeholder>
                <PlaceholderText>Vyžaduje sa overenie druhým faktorom.</PlaceholderText>
                <Primary type="button" onClick={() => dispatch(openStepUp({ blocking: true }))}>
                    Overiť
                </Primary>
            </Placeholder>
        );
    }

    return (
        <>
            {nudge && !mfa.enabled && !nudgeDismissed && (
                <Notice>
                    <Banner $warn>
                        Zapnite dvojstupňové overenie pre svoj správcovský účet — chráni faktúry,
                        kampane a údaje členov.{' '}
                        <LinkButton type="button" onClick={() => navigate('/bezpecnost')}>
                            Nastaviť teraz
                        </LinkButton>
                        {' · '}
                        <LinkButton type="button" onClick={dismissNudge}>
                            Skryť
                        </LinkButton>
                    </Banner>
                </Notice>
            )}
            {children}
        </>
    );
};

/* ---------------- styles (folder-local copies of the Campaigns vocabulary) ---------------- */

const Placeholder = styled.div`
    padding: 28px 20px;
    text-align: center;
    font-size: 14px;
    line-height: 1.6;
    color: #777;
`;

const PlaceholderText = styled.p`
    margin: 0 0 14px;
`;

/** The pages this wraps supply their own padding; a banner rendered before them has none. */
const Notice = styled.div`
    padding: 16px 20px 0;
`;

const Banner = styled.p<{ $ok?: boolean; $warn?: boolean }>`
    background: ${(p) => (p.$warn ? '#fff4e5' : p.$ok ? '#eef7ee' : '#fdecec')};
    color: ${(p) => (p.$warn ? '#8a5300' : p.$ok ? '#256029' : '#a11')};
    border-radius: 8px;
    padding: 12px 16px;
    font-size: 13.5px;
    line-height: 1.6;
    margin: 0 0 10px;
`;

const Secondary = styled.button`
    font: inherit;
    font-size: 14px;
    font-weight: 600;
    padding: 11px 20px;
    border-radius: 5px;
    border: 1px solid #ddd;
    background: #fff;
    color: #141210;
    cursor: pointer;

    &:disabled {
        opacity: 0.6;
        cursor: default;
    }
`;

const Primary = styled(Secondary)`
    background: #f56607;
    border-color: #f56607;
    color: #fff;
`;

const LinkButton = styled.button`
    background: none;
    border: none;
    color: inherit;
    font: inherit;
    font-weight: 700;
    text-decoration: underline;
    cursor: pointer;
    padding: 0;
`;

export default StepUpGate;
